import React, { useEffect, useState } from "react";
import { AdminAppShell } from "../design-system/shells/AdminAppShell";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

function money(n) { return `$${Number(n||0).toLocaleString()}`; }

export default function Reports() {
  const { user } = useAuth();
  const [opps, setOpps] = useState([]);
  const [leads, setLeads] = useState([]);
  useEffect(() => {
    api.get("/opportunities").then((r) => setOpps(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  }, []);

  // Pipeline value by stage
  const stages = ["qualified","site_visit","estimating","proposal","negotiation","won","lost"];
  const pipeline = stages.map((s) => ({
    stage: s,
    count: opps.filter((o) => o.stage === s).length,
    value: opps.filter((o) => o.stage === s).reduce((a, o) => a + (o.expected_value || 0), 0),
  }));
  const bySource = Object.entries(
    leads.reduce((a, l) => { a[l.source || "unknown"] = (a[l.source || "unknown"] || 0) + 1; return a; }, {})
  ).map(([k, v]) => ({ source: k, count: v }));

  return (
    <AdminAppShell activeNav="reports" title="Reports" subtitle="Real-data reporting · empty when no data has been entered" user={user}>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-erf-borderCream" data-testid="report-pipeline">
          <CardContent className="p-5">
            <div className="font-display font-semibold text-erf-dark mb-3">Pipeline value by stage</div>
            {opps.length === 0 ? <div className="text-xs font-mono text-erf-dark/50">No opportunities yet.</div> : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={pipeline}>
                  <XAxis dataKey="stage" tick={{ fontSize: 10, fontFamily: "monospace" }} />
                  <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} />
                  <Tooltip formatter={(v, k) => k === "value" ? money(v) : v} />
                  <Bar dataKey="value" fill="#B8892B" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card className="border-erf-borderCream" data-testid="report-source">
          <CardContent className="p-5">
            <div className="font-display font-semibold text-erf-dark mb-3">Leads by source</div>
            {leads.length === 0 ? <div className="text-xs font-mono text-erf-dark/50">No leads yet.</div> : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={bySource}>
                  <XAxis dataKey="source" tick={{ fontSize: 10, fontFamily: "monospace" }} />
                  <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0F0F10" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card className="border-erf-borderCream lg:col-span-2" data-testid="report-summary">
          <CardContent className="p-5 grid grid-cols-4 gap-4">
            <div><div className="text-[11px] font-mono uppercase text-erf-dark/60">Total leads</div><div className="font-mono text-2xl">{leads.length}</div></div>
            <div><div className="text-[11px] font-mono uppercase text-erf-dark/60">Won opps</div><div className="font-mono text-2xl">{opps.filter(o=>o.stage==="won").length}</div></div>
            <div><div className="text-[11px] font-mono uppercase text-erf-dark/60">Pipeline value</div><div className="font-mono text-2xl">{money(opps.filter(o=>!["won","lost"].includes(o.stage)).reduce((a,o)=>a+(o.expected_value||0),0))}</div></div>
            <div><div className="text-[11px] font-mono uppercase text-erf-dark/60">Weighted forecast</div><div className="font-mono text-2xl">{money(opps.reduce((a,o)=>a+(o.expected_value||0)*(o.probability||0),0))}</div></div>
          </CardContent>
        </Card>
      </div>
    </AdminAppShell>
  );
}
